/**
 * HERMES Protocol — 3D Vendor Showroom
 * Direct Three.js rendering (same pattern as MetaverseViewerPage)
 * Route: /vendors/:vendorId/showroom
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { createBitsyAvatar } from '@/components/metaverse/BitsyAvatar';
import MetaverseChat from '@/components/metaverse/MetaverseChat';
import {
  ArrowLeft, Package, ShoppingBag, Loader2, RotateCcw, Eye,
  ExternalLink, ShieldCheck, Bed, DoorOpen, Tag, MessageCircle, BarChart3,
  Gift, X
} from 'lucide-react';
import { BACKEND_URL, API } from '@/lib/backendUrl';


// ═══════════════════════════════════════════════════════════
// 3D SHOWROOM SCENE
// ═══════════════════════════════════════════════════════════

function buildShowroomScene(scene, showroomData, onProductClick) {
  const { products, showroom_config: cfg } = showroomData;
  const { w, h, d } = cfg.gallery_size;

  // Floor
  const floorGeo = new THREE.PlaneGeometry(w + 4, d + 4);
  const floorMat = new THREE.MeshStandardMaterial({ color: cfg.floor_color, roughness: 0.9 });
  const floor = new THREE.Mesh(floorGeo, floorMat);
  floor.rotation.x = -Math.PI / 2;
  floor.receiveShadow = true;
  scene.add(floor);

  // Walls
  const wallMat = new THREE.MeshStandardMaterial({
    color: cfg.ambient_color, side: THREE.DoubleSide, roughness: 0.8,
  });
  // Back wall
  const backWall = new THREE.Mesh(new THREE.PlaneGeometry(w + 4, h), wallMat);
  backWall.position.set(0, h / 2, -d / 2);
  scene.add(backWall);
  // Side walls
  const sideWall1 = new THREE.Mesh(new THREE.PlaneGeometry(d + 4, h), wallMat);
  sideWall1.rotation.y = Math.PI / 2;
  sideWall1.position.set(-w / 2 - 2, h / 2, 0);
  scene.add(sideWall1);
  const sideWall2 = sideWall1.clone();
  sideWall2.position.set(w / 2 + 2, h / 2, 0);
  scene.add(sideWall2);

  // Ceiling with subtle glow
  const ceilingMat = new THREE.MeshStandardMaterial({ color: '#0a0a14', roughness: 1 });
  const ceiling = new THREE.Mesh(new THREE.PlaneGeometry(w + 4, d + 4), ceilingMat);
  ceiling.rotation.x = Math.PI / 2;
  ceiling.position.y = h;
  scene.add(ceiling);

  // Ambient light
  scene.add(new THREE.AmbientLight(0x404060, 0.4));

  // Brand accent strip on back wall
  const stripGeo = new THREE.PlaneGeometry(w + 2, 0.08);
  const stripMat = new THREE.MeshBasicMaterial({ color: cfg.accent_color });
  const strip = new THREE.Mesh(stripGeo, stripMat);
  strip.position.set(0, h * 0.85, -d / 2 + 0.01);
  scene.add(strip);

  // Product pedestals + items
  const productMeshes = [];
  const count = products.length;
  const spacing = Math.min((w - 2) / Math.max(count, 1), 3.5);
  const startX = -((count - 1) * spacing) / 2;

  products.forEach((product, i) => {
    const x = startX + i * spacing;
    const z = 0;
    const cfg3d = product.display_3d;

    // Pedestal
    const pedestalH = cfg3d.pedestal_h || 0.8;
    const pedestalGeo = new THREE.CylinderGeometry(0.5, 0.55, pedestalH, 32);
    const pedestalMat = new THREE.MeshStandardMaterial({
      color: cfg.pedestal_color, roughness: 0.3, metalness: 0.6,
    });
    const pedestal = new THREE.Mesh(pedestalGeo, pedestalMat);
    pedestal.position.set(x, pedestalH / 2, z);
    pedestal.castShadow = true;
    scene.add(pedestal);

    // Spotlight per product
    const spot = new THREE.SpotLight(0xffffff, 2, 6, Math.PI / 6, 0.5);
    spot.position.set(x, h - 0.3, z);
    spot.target.position.set(x, pedestalH, z);
    spot.castShadow = true;
    scene.add(spot);
    scene.add(spot.target);

    // Product mesh
    let productGeo;
    const [sx, sy, sz] = cfg3d.scale;
    if (cfg3d.shape === 'cylinder') {
      productGeo = new THREE.CylinderGeometry(sx, sx, sy, 32);
    } else if (cfg3d.shape === 'sphere') {
      productGeo = new THREE.SphereGeometry(sx, 32, 32);
    } else {
      productGeo = new THREE.BoxGeometry(sx, sy, sz);
    }

    const productMat = new THREE.MeshStandardMaterial({
      color: cfg3d.color, roughness: 0.4, metalness: 0.1,
    });
    const productMesh = new THREE.Mesh(productGeo, productMat);
    productMesh.position.set(x, pedestalH + sy / 2 + 0.02, z);
    productMesh.castShadow = true;
    productMesh.userData = { productIndex: i, product };
    scene.add(productMesh);
    productMeshes.push(productMesh);

    // Glow ring at pedestal base
    const ringGeo = new THREE.RingGeometry(0.52, 0.58, 32);
    const ringMat = new THREE.MeshBasicMaterial({
      color: cfg.accent_color, transparent: true, opacity: 0.6, side: THREE.DoubleSide,
    });
    const ring = new THREE.Mesh(ringGeo, ringMat);
    ring.rotation.x = -Math.PI / 2;
    ring.position.set(x, 0.01, z);
    scene.add(ring);
  });

  return productMeshes;
}


function useShowroomScene(containerRef, showroomData, onProductClick, bitsyMoodRef) {
  const sceneRef = useRef(null);
  const productMeshesRef = useRef([]);
  const animFrameRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current || !showroomData) return;
    const container = containerRef.current;
    const w = container.clientWidth;
    const h = container.clientHeight;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color('#06060f');
    scene.fog = new THREE.Fog('#06060f', 8, 18);

    const camera = new THREE.PerspectiveCamera(50, w / h, 0.1, 50);
    camera.position.set(0, 2.5, 6);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
    renderer.setSize(w, h);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.2;
    container.appendChild(renderer.domElement);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.maxPolarAngle = Math.PI / 2.05;
    controls.minDistance = 2;
    controls.maxDistance = 12;
    controls.target.set(0, 1, 0);

    // Build showroom
    const productMeshes = buildShowroomScene(scene, showroomData, onProductClick);
    productMeshesRef.current = productMeshes;
    sceneRef.current = scene;

    // Add Bitsy
    let bitsyObj = null;
    try {
      const bitsyX = showroomData.products.length > 0
        ? -((showroomData.products.length - 1) * Math.min((showroomData.showroom_config.gallery_size.w - 2) / Math.max(showroomData.products.length, 1), 3.5)) / 2 - 1.8
        : -2;
      bitsyObj = createBitsyAvatar(scene, { x: bitsyX, y: 0, z: 1.5 });
      if (bitsyObj?.group) {
        bitsyObj.group.scale.set(0.7, 0.7, 0.7);
      }
    } catch (e) {
      console.warn('Bitsy avatar not available:', e);
    }

    // Raycaster for product clicks
    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();
    const handleClick = (e) => {
      const rect = renderer.domElement.getBoundingClientRect();
      mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(mouse, camera);
      const hits = raycaster.intersectObjects(productMeshes);
      if (hits.length > 0 && hits[0].object.userData.product) {
        onProductClick?.(hits[0].object.userData.product);
      }
    };
    renderer.domElement.addEventListener('click', handleClick);

    // Hover cursor
    const handleHover = (e) => {
      const rect = renderer.domElement.getBoundingClientRect();
      mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(mouse, camera);
      const hits = raycaster.intersectObjects(productMeshes);
      renderer.domElement.style.cursor = hits.length > 0 ? 'pointer' : 'default';
    };
    renderer.domElement.addEventListener('mousemove', handleHover);

    // Animation
    const clock = new THREE.Clock();
    const animate = () => {
      animFrameRef.current = requestAnimationFrame(animate);
      const t = clock.getElapsedTime();
      controls.update();

      // Gentle product rotation
      productMeshes.forEach((mesh, i) => {
        mesh.rotation.y = Math.sin(t * 0.5 + i * 1.2) * 0.15;
        mesh.position.y = mesh.userData.product?.display_3d?.pedestal_h + mesh.userData.product?.display_3d?.scale[1] / 2 + 0.02 + Math.sin(t * 0.8 + i) * 0.02;
      });

      // Animate Bitsy
      if (bitsyObj?.animate) bitsyObj.animate(t);

      renderer.render(scene, camera);
    };
    animate();

    // Resize
    const handleResize = () => {
      const nw = container.clientWidth;
      const nh = container.clientHeight;
      camera.aspect = nw / nh;
      camera.updateProjectionMatrix();
      renderer.setSize(nw, nh);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(animFrameRef.current);
      window.removeEventListener('resize', handleResize);
      renderer.domElement.removeEventListener('click', handleClick);
      renderer.domElement.removeEventListener('mousemove', handleHover);
      controls.dispose();
      renderer.dispose();
      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
    };
  }, [containerRef, showroomData, onProductClick, bitsyMoodRef]);
}

// ═══════════════════════════════════════════════════════════
// PRODUCT DETAIL PANEL
// ═══════════════════════════════════════════════════════════

function ProductDetailPanel({ product, onClose, pricing }) {
  if (!product) return null;
  const dp = pricing?.products?.find(p => p.product_id === product.product_id);
  return (
    <div className="absolute top-4 right-4 w-72 z-20 animate-in slide-in-from-right-4 duration-300" data-testid="product-detail-panel">
      <Card className="bg-[#0a0f1a]/95 backdrop-blur-xl border-[#F7931A]/20 shadow-2xl">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-sm font-bold text-white">{product.name}</h3>
              <p className="text-[10px] text-gray-400">{product.model || product.sku || product.category}</p>
            </div>
            <button onClick={onClose} className="text-gray-500 hover:text-white text-xs">X</button>
          </div>

          <Badge className="bg-[#F7931A]/10 text-[#F7931A] border-[#F7931A]/20 text-[9px]">
            {product.category}
          </Badge>

          {/* Dynamic Pricing Display — Clean and subtle */}
          {dp?.has_discount ? (
            <div data-testid="dynamic-price">
              <div className="flex items-baseline gap-2">
                <p className="text-2xl font-bold text-white heading-font">${dp.final_price.toLocaleString()}</p>
                <p className="text-xs text-gray-500 line-through">${dp.original_price.toLocaleString()}</p>
              </div>
              {dp.primary_label && (
                <p className="text-[9px] text-gray-400 mt-1">{dp.primary_label}</p>
              )}
            </div>
          ) : product.unit_cost > 0 ? (
            <div>
              <p className="text-2xl font-bold text-white heading-font">${product.unit_cost.toLocaleString()}</p>
              <p className="text-[9px] text-gray-500">Free delivery</p>
            </div>
          ) : null}

          {product.description && (
            <p className="text-[10px] text-gray-400 leading-relaxed">{product.description}</p>
          )}

          <div className="flex items-center gap-3 text-[9px] text-gray-500">
            <div className="flex items-center gap-1">
              <DoorOpen className="w-3 h-3" />
              {product.rooms_installed} room{product.rooms_installed !== 1 ? 's' : ''}
            </div>
            <div className="flex items-center gap-1">
              <ShieldCheck className="w-3 h-3 text-emerald-400" />
              On-chain SBT
            </div>
          </div>

          {product.qr_links?.length > 0 && (
            <a
              href={product.qr_links[0].qr_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full py-2 bg-[#F7931A] hover:bg-[#e8841a] text-black rounded-lg text-xs font-semibold transition-colors"
              data-testid="buy-product-btn"
            >
              <ShoppingBag className="w-3.5 h-3.5" />
              Buy for Home
              <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════

export default function VendorShowroomPage() {
  const { vendorId } = useParams();
  const containerRef = useRef(null);
  const bitsyMoodRef = useRef('idle');
  const wsRef = useRef(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [liveChat, setLiveChat] = useState([]);
  const [liveChatInput, setLiveChatInput] = useState('');
  const [activeOffer, setActiveOffer] = useState(null);
  const [dynamicPricing, setDynamicPricing] = useState(null);
  const [bundles, setBundles] = useState([]);

  // Browsing signal tracking
  const signalTracker = useRef({});
  const productTimers = useRef({});

  const visitorId = useRef(localStorage.getItem('bitsy_guest_id') || `v_${Date.now().toString(36)}`).current;
  const fromQr = useRef(new URLSearchParams(window.location.search).get('from') === 'qr').current;

  // Send intent signals periodically
  const sendIntentSignal = useCallback((productId, productName) => {
    const tracker = signalTracker.current[productId];
    if (!tracker || !wsRef.current || wsRef.current.readyState !== 1) return;
    wsRef.current.send(JSON.stringify({
      action: 'intent_signal', visitor_id: visitorId, product_id: productId, product_name: productName,
      time_on_product: tracker.time || 0, click_count: tracker.clicks || 0,
      price_checks: tracker.priceChecks || 0, zoom_events: tracker.zooms || 0,
      comparisons: Object.keys(signalTracker.current).length, return_visits: tracker.returnVisits || 0,
    }));
  }, [visitorId]);

  // WebSocket presence connection
  useEffect(() => {
    const wsUrl = API.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/api/ws/showroom/${vendorId}`);
    wsRef.current = ws;

    ws.onopen = () => {
      ws.send(JSON.stringify({ action: 'join', visitor_id: visitorId, role: 'guest', from_qr: fromQr, display_name: `Guest ${visitorId.slice(0, 6)}` }));
    };
    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        if (msg.type === 'chat') {
          // Check if it's an offer — only show once per session
          if (msg.data?.offer) {
            const offerShown = sessionStorage.getItem(`offer_shown_${vendorId}`);
            if (!offerShown) {
              setActiveOffer(msg.data);
              sessionStorage.setItem(`offer_shown_${vendorId}`, 'true');
            }
          } else {
            setLiveChat(prev => [...prev.slice(-30), msg.data]);
          }
        }
      } catch {}
    };

    const pingInterval = setInterval(() => { if (ws.readyState === 1) ws.send('ping'); }, 25000);

    return () => {
      clearInterval(pingInterval);
      if (ws.readyState === 1) ws.send(JSON.stringify({ action: 'leave', visitor_id: visitorId }));
      ws.close();
    };
  }, [vendorId, visitorId, fromQr]);

  useEffect(() => {
    fetch(`${API}/api/vendor/${vendorId}/showroom`)
      .then(r => r.json())
      .then(d => {
        setData(d);
        // Fetch dynamic pricing based on guest context
        const searchParams = new URLSearchParams(window.location.search);
        const guestCtx = {
          room_type: searchParams.get('room') || 'standard',
          stay_nights: parseInt(searchParams.get('nights') || '1'),
          visit_count: parseInt(localStorage.getItem(`showroom_visits_${vendorId}`) || '0') + 1,
          intent_score: 0,
          visitor_id: visitorId,
        };
        localStorage.setItem(`showroom_visits_${vendorId}`, String(guestCtx.visit_count));
        fetch(`${API}/api/vendor/pricing/calculate`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ vendor_id: vendorId, guest: guestCtx }),
        }).then(r => r.json())
          .then(p => { setDynamicPricing(p); setBundles(p.bundles || []); })
          .catch(() => {});
        // Track showroom view
        fetch(`${API}/api/vendor/analytics/track`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ vendor_id: vendorId, event_type: 'showroom_view', visitor_id: visitorId }),
        }).catch(() => {});
        if (fromQr) {
          fetch(`${API}/api/vendor/analytics/track`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ vendor_id: vendorId, event_type: 'showroom_from_qr', visitor_id: visitorId }),
          }).catch(() => {});
        }
      })
      .catch(() => toast.error('Failed to load showroom'))
      .finally(() => setLoading(false));
  }, [vendorId, visitorId, fromQr]);

  const handleProductClick = useCallback((product) => {
    setSelectedProduct(product);
    const pid = product.product_id;

    // Initialize tracker for this product
    if (!signalTracker.current[pid]) {
      signalTracker.current[pid] = { clicks: 0, priceChecks: 0, zooms: 0, time: 0, returnVisits: 0 };
    }
    const tracker = signalTracker.current[pid];
    tracker.clicks += 1;
    tracker.priceChecks += 1; // opening detail panel = price check

    // Track return visits
    if (tracker.lastLeft) {
      tracker.returnVisits += 1;
    }

    // Start timing this product
    if (productTimers.current[pid]) clearInterval(productTimers.current[pid]);
    productTimers.current[pid] = setInterval(() => {
      signalTracker.current[pid].time += 1;
      // Send intent every 5 seconds while viewing
      if (signalTracker.current[pid].time % 5 === 0) {
        sendIntentSignal(pid, product.name);
      }
    }, 1000);

    // Track product click analytics
    fetch(`${API}/api/vendor/analytics/track`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ vendor_id: vendorId, event_type: 'product_click', product_id: pid, visitor_id: visitorId }),
    }).catch(() => {});
    // Broadcast product focus via WS
    if (wsRef.current?.readyState === 1) {
      wsRef.current.send(JSON.stringify({ action: 'product_focus', visitor_id: visitorId, product_name: product.name, product_id: pid }));
    }
  }, [vendorId, visitorId, sendIntentSignal]);

  // Stop timing when product is deselected
  const handleCloseProduct = useCallback(() => {
    if (selectedProduct) {
      const pid = selectedProduct.product_id;
      if (productTimers.current[pid]) {
        clearInterval(productTimers.current[pid]);
        productTimers.current[pid] = null;
      }
      if (signalTracker.current[pid]) {
        signalTracker.current[pid].lastLeft = Date.now();
        sendIntentSignal(pid, selectedProduct.name);
      }
    }
    setSelectedProduct(null);
  }, [selectedProduct, sendIntentSignal]);

  const sendLiveChat = useCallback(() => {
    if (!liveChatInput.trim() || wsRef.current?.readyState !== 1) return;
    wsRef.current.send(JSON.stringify({ action: 'chat', sender_id: visitorId, sender_name: `Guest ${visitorId.slice(0, 6)}`, sender_role: 'guest', text: liveChatInput.trim() }));
    setLiveChatInput('');
  }, [liveChatInput, visitorId]);

  const handleBitsyMood = useCallback((mood) => {
    bitsyMoodRef.current = mood;
  }, []);

  useShowroomScene(containerRef, data, handleProductClick, bitsyMoodRef);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030712] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#F7931A]" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#030712] flex items-center justify-center text-gray-500">
        Vendor showroom not found
      </div>
    );
  }

  return (
    <div className="h-screen bg-[#030712] text-white flex flex-col" data-testid="vendor-showroom">
      {/* Header */}
      <div className="border-b border-white/5 bg-[#0a0f1a]/80 backdrop-blur-sm z-10 shrink-0">
        <div className="max-w-full px-4 py-2.5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/vendors" className="p-1.5 rounded-lg hover:bg-white/5 transition-colors" data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 text-gray-400" />
            </Link>
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-[#F7931A]/10 border border-[#F7931A]/20 flex items-center justify-center">
                <Package className="w-3.5 h-3.5 text-[#F7931A]" />
              </div>
              <div>
                <h1 className="text-sm font-bold heading-font">{data.vendor.company_name}</h1>
                <div className="flex items-center gap-1.5">
                  {data.vendor.categories?.map(cat => (
                    <Badge key={cat} variant="outline" className="text-[7px] h-3.5 border-purple-500/20 text-purple-400 py-0">
                      {cat}
                    </Badge>
                  ))}
                  {data.vendor.sbt_minted && (
                    <Badge variant="outline" className="text-[7px] h-3.5 border-emerald-500/20 text-emerald-400 py-0">
                      <ShieldCheck className="w-2.5 h-2.5 mr-0.5" /> SBT #{data.vendor.token_id}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <p className="text-[10px] text-gray-500 hidden sm:block">Click products to view details</p>
            <Link
              to={`/vendors/${vendorId}/analytics`}
              className="inline-flex items-center gap-1.5 text-xs h-7 px-3 rounded-md border border-white/10 text-gray-400 hover:bg-white/5 transition-colors"
              data-testid="analytics-link"
            >
              <BarChart3 className="w-3 h-3" />
              Analytics
            </Link>
            <Button
              size="sm" variant="outline"
              className="text-xs gap-1.5 h-7 border-white/10 text-gray-400 hover:bg-white/5"
              onClick={() => setChatOpen(!chatOpen)}
              data-testid="toggle-chat-btn"
            >
              <MessageCircle className="w-3 h-3" />
              {chatOpen ? 'Close' : 'Ask Bitsy'}
            </Button>
          </div>
        </div>
      </div>

      {/* 3D Viewport */}
      <div className="flex-1 relative overflow-hidden">
        <div ref={containerRef} className="w-full h-full" data-testid="showroom-canvas" />

        {/* Subtle personalized pricing indicator — only a small tag, not a banner */}
        {dynamicPricing?.summary?.total_savings > 0 && (
          <div className="absolute top-16 left-4 z-10" data-testid="pricing-banner">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white/5 backdrop-blur text-[9px] text-gray-400 border border-white/5">
              <Tag className="w-2.5 h-2.5" />
              Your personalized pricing is active
            </div>
          </div>
        )}

        {/* Bundle suggestions — only show after user has clicked a product */}
        {bundles.length > 0 && selectedProduct && (
          <div className="absolute top-16 right-4 z-10 w-52" data-testid="bundle-cards">
            {bundles.slice(0, 1).map((b) => (
              <div key={b.bundle_id} className="p-2.5 rounded-lg bg-[#0a0f1a]/80 backdrop-blur border border-white/5">
                <p className="text-[9px] text-gray-400">{b.name}</p>
                <div className="flex items-baseline gap-1.5 mt-0.5">
                  <span className="text-xs font-medium text-white">${b.bundle_price.toLocaleString()}</span>
                  <span className="text-[9px] text-gray-600 line-through">${b.original_total.toLocaleString()}</span>
                </div>
                <p className="text-[8px] text-gray-600 mt-0.5">{b.product_count} items together</p>
              </div>
            ))}
          </div>
        )}

        {/* Product Detail Panel */}
        <ProductDetailPanel
          product={selectedProduct}
          onClose={handleCloseProduct}
          pricing={dynamicPricing}
        />

        {/* Product Count */}
        <div className="absolute bottom-4 left-4 z-10">
          <Badge className="bg-[#0a0f1a]/80 backdrop-blur text-gray-300 border-white/10 text-[10px] gap-1.5 px-3 py-1" data-testid="product-count">
            <Eye className="w-3 h-3" />
            {data.products.length} product{data.products.length !== 1 ? 's' : ''} on display
          </Badge>
        </div>

        {/* Product Quick Nav */}
        <div className="absolute bottom-4 right-4 z-10 flex gap-1.5">
          {data.products.map((p, i) => (
            <button
              key={p.product_id}
              onClick={() => setSelectedProduct(p)}
              className={`px-2.5 py-1 rounded-lg text-[9px] font-medium border backdrop-blur transition-all ${
                selectedProduct?.product_id === p.product_id
                  ? 'bg-[#F7931A]/20 border-[#F7931A]/40 text-[#F7931A]'
                  : 'bg-[#0a0f1a]/70 border-white/10 text-gray-400 hover:border-white/20'
              }`}
              data-testid={`nav-product-${i}`}
            >
              {p.name.length > 15 ? p.name.slice(0, 15) + '...' : p.name}
            </button>
          ))}
        </div>

        {/* Bitsy Chat Overlay */}
        {chatOpen && (
          <div className="absolute top-2 left-4 z-20 w-80">
            <MetaverseChat
              hotelId={vendorId}
              roomType="showroom"
              onMoodChange={handleBitsyMood}
              isOpen={chatOpen}
            />
          </div>
        )}

        {/* Live Chat Messages (from vendor) */}
        {liveChat.length > 0 && (
          <div className="absolute top-14 right-4 z-20 w-72 max-h-48 overflow-y-auto space-y-1.5 scrollbar-thin" data-testid="live-chat-messages">
            {liveChat.slice(-5).map((msg, i) => (
              <div key={i} className={`p-2 rounded-lg backdrop-blur text-[10px] border animate-in slide-in-from-right ${
                msg.sender_role === 'vendor'
                  ? 'bg-[#F7931A]/10 border-[#F7931A]/20 text-[#F7931A]'
                  : 'bg-white/5 border-white/10 text-gray-300'
              }`}>
                <span className="font-bold">{msg.sender_name}:</span> {msg.text}
              </div>
            ))}
          </div>
        )}

        {/* Guest Chat Input */}
        <div className="absolute top-2 right-4 z-20 flex items-center gap-1.5" data-testid="guest-chat-bar">
          <input
            type="text"
            placeholder="Message vendor..."
            value={liveChatInput}
            onChange={e => setLiveChatInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && sendLiveChat()}
            className="w-44 bg-[#0a0f1a]/80 backdrop-blur border border-white/10 rounded-lg px-2.5 py-1.5 text-[10px] text-white placeholder:text-gray-600 focus:border-[#F7931A]/30 focus:outline-none"
            data-testid="guest-chat-input"
          />
          <button
            onClick={sendLiveChat}
            className="p-1.5 rounded-lg bg-[#F7931A]/10 border border-[#F7931A]/20 text-[#F7931A] hover:bg-[#F7931A]/20 transition-colors"
            data-testid="guest-chat-send"
          >
            <ArrowLeft className="w-3 h-3 rotate-180" />
          </button>
        </div>

        {/* Vendor Offer Popup */}
        {activeOffer && (
          <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/40 backdrop-blur-sm" data-testid="offer-overlay">
            <div className="w-80 bg-[#0a0f1a] border border-[#F7931A]/30 rounded-xl p-5 shadow-2xl animate-in zoom-in-95">
              <button
                onClick={() => setActiveOffer(null)}
                className="absolute top-3 right-3 p-1 rounded hover:bg-white/10 text-gray-400"
                data-testid="close-offer"
              >
                <X className="w-4 h-4" />
              </button>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-10 h-10 rounded-lg bg-[#F7931A]/10 border border-[#F7931A]/20 flex items-center justify-center">
                  <Gift className="w-5 h-5 text-[#F7931A]" />
                </div>
                <div>
                  <p className="text-sm font-bold text-[#F7931A] heading-font">Special Offer!</p>
                  <p className="text-[9px] text-gray-500">From the vendor, just for you</p>
                </div>
              </div>
              <div className="p-3 rounded-lg bg-[#F7931A]/5 border border-[#F7931A]/10 mb-3">
                <p className="text-xs text-white leading-relaxed">{activeOffer.text}</p>
                {activeOffer.offer && (
                  <div className="mt-2 flex items-center gap-2">
                    <Badge className="bg-[#F7931A]/20 text-[#F7931A] border-[#F7931A]/30 text-sm font-bold">{activeOffer.offer.discount_pct}% OFF</Badge>
                    <span className="text-[10px] text-gray-400">{activeOffer.offer.product_name}</span>
                  </div>
                )}
              </div>
              <Button
                size="sm"
                className="w-full bg-[#F7931A] text-black font-bold hover:bg-[#F7931A]/90 gap-1.5"
                onClick={() => { setActiveOffer(null); toast.success('Offer claimed! Discount applied.'); }}
                data-testid="claim-offer-btn"
              >
                <Gift className="w-3.5 h-3.5" /> Claim This Offer
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
